import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GoogleMap, MarkerF, useJsApiLoader, OverlayViewF } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { fetchAssetsLocationDetails, WellLocation } from '../../components/slices/AssetLocationsSlice';
import './WellLocations.scss';
import HeaderElements from '../navigation/components/HeaderElements';
import { useAppSelector, useAppDispatch } from '../../hooks/storeHooks';

import Loader from '../common/Loader/Loader';
import { assetIconMarkup } from './AssetIconsMap';
import EmptyState from '../../components/empty-state/EmptyState';
import MultiSelectFormField from '../../components/common/MultiSelectField';
import { ReactComponent as SliderCollapse } from '../../images/slider-collapse.svg';
import { ReactComponent as SliderExpand } from '../../images/slider-expand.svg';
import { ReactComponent as FullScreenIcon } from '../../images/expand-map.svg';
import { ReactComponent as ZoomInIcon } from '../../images/zoom-in.svg';
import { ReactComponent as ZoomOutIcon } from '../../images/zoom-out.svg';
import RodLiftStatusOverviewTable from '../../components/rod-lift-status-overview-table/RodLiftStatusOverviewTable';

import { fetchInitialStateAsync } from '../../features/asset-status/AssetStatusSlice';

import MenuNavIcon from '../navigation/components/NavMenuIcon';
import GlobalWellHistorySearch from '../navigation/components/GlobalWellHistorySearch';
import { useToastService } from '../../components/toast/ToastService';
import ReactDOMServer from 'react-dom/server';

const RUN_STATUS_COLOR_ENTRIES: Array<[string, string]> = [
  ['communication failure', '#EF681F'],
  ['running', '#12B669'],
  ['shutdown', '#D92C20'],
  ['idle', '#FDB022'],
  ['disabled', '#9AA4B2'],
];
const DEFAULT_RUN_STATUS_COLOR = '#667085';

const wellStatuses = [
  { value: 'running', key: 'Running' },
  { value: 'shutdown', key: 'Shutdown' },
  { value: 'idle', key: 'Idle' },
  { value: 'communication failure', key: 'Communication failure' },
  { value: 'disabled', key: 'Disabled' },
];

// Legend items configuration
const LEGEND_ITEMS: any[] = [];
// Consistent overlay arrow sizing (pixels)
const OVERLAY_ARROW_SIZE = 12; // square rotated 45deg
// Pixel offset from custom pin anchor (bottom tip) to icon visual center
// Our custom icon uses scaledSize 36x48 with anchor (18,48) and inner center at (18,18) => 30px upward
const MARKER_CENTER_OFFSET_Y = 30;
// Extra horizontal space between marker icon edge and tooltip tail tip
const TOOLTIP_TAIL_GAP_X = 8;

const WellLocations = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { showErrorToast } = useToastService();

  const isRodliftwellStatusLoading = useAppSelector((state: any) => state.rodliftwellstatus?.loading ?? false);
  const rodliftwellstatusData = useAppSelector((state: any) => state.rodliftwellstatus?.data?.statusRegisters ?? []);

  const { wellLocations, loading } = useAppSelector((state) => state.AssetLocationsSlice);
  const assets = useAppSelector((state) => state.assetGroups?.assets);

  const [wellStatusesFilter, setWellStatusesFilter] = useState<string[]>([]);
  const [mapType, setMapType] = useState<string>('Map');
  // Marker interaction states
  const [clickedMarker, setClickedMarker] = useState<WellLocation | null>(null);
  const [showPeak, setShowPeak] = useState<boolean>(false);
  const [hoveredMarker, setHoveredMarker] = useState<
    (WellLocation & { lat: number; lng: number; __reactKey?: string }) | null
  >(null);

  // Indicates if there is at least one valid marker with usable coordinates
  const [hasValidMarkers, setHasValidMarkers] = useState(false);
  const hoverTimeoutRef = useRef<number | null>(null);
  const activeMarker = clickedMarker;
  const mapRef = useRef<google.maps.Map | null>(null);
  // Keep a reference to world/restriction bounds and dynamic minZoom
  const restrictionBoundsRef = useRef<google.maps.LatLngBounds | null>(null);
  const RESTRICTION_BOUNDS_OBJ = useRef({
    north: 85,
    south: -60,
    west: -179.999,
    east: 179.999,
  });

  const [visibleStatuses, setVisibleStatuses] = useState<Set<string>>(
    new Set(['running', 'shutdown', 'idle', 'communication failure', 'disabled']),
  );
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchedAsset, setSearchedAsset] = useState<{ id: any; name: string } | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_Google_Maps_API_KEY || 'AIzaSyBdGrNpaLM_WTXPpNGrBjCkTyVk-c9zyOU', // It's safer to use your actual key
  });

  const getNormalizedStatus = (runStatus?: string): string => {
    if (!runStatus) return 'unknown';
    const lower = runStatus.toLowerCase();
    if (lower.includes('running')) return 'running';
    if (lower.includes('shutdown')) return 'shutdown';
    if (lower.includes('idle')) return 'idle';
    if (lower.includes('communication failure')) return 'communication failure';
    if (lower.includes('disabled')) return 'disabled';
    return 'unknown';
  };

  /*
   * Check if any markers have valid coordinates
   * Returns true if any markers found with valid lat/lng, false if all are invalid
   * latitude and longitude are both present and numeric
   * latitude is between -90 and 90
   * longitude is between -180 and 180
   * not both latitude and longitude equal to 0 (treated as placeholder)
   */
  const findValidMarkers = (wellLocations: WellLocation[]): boolean => {
    // Returns true if ANY record has valid lat/lng coordinates.
    // Valid means: both present, numeric, within range, and not the (0,0) placeholder.
    return wellLocations.some((well) => {
      const lat = well?.latitude != null ? Number(well.latitude) : NaN;
      const lng = well?.longitude != null ? Number(well.longitude) : NaN;
      if (isNaN(lat) || isNaN(lng)) return false;
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
      if (lat === 0 && lng === 0) return false;
      return true; // this marker is valid
    });
  };

  // --- 1. DATA SIMPLIFICATION: Derive markers directly from props on each render. ---
  // This removes the need for `wellLocationsArray` and `markers` state, fixing the loading inconsistency.
  const validMarkers = React.useMemo(() => {
    if (!Array.isArray(wellLocations)) return [];
    setHasValidMarkers(findValidMarkers(wellLocations));
    // Build list while preserving first occurrence per (assetGuid, lat, lng) triple.
    const seen = new Set<string>();
    const result: (WellLocation & { lat: number; lng: number; __reactKey: string })[] = [];
    let locationsToProcess = wellLocations;
    if (searchedAsset) {
      locationsToProcess = locationsToProcess.filter((loc) => loc.assetGuid === searchedAsset.id);
    }
    if (wellStatusesFilter?.length > 0) {
      locationsToProcess = locationsToProcess.filter((loc) => {
        const rs = (loc.runStatus || '').toLowerCase();
        return wellStatusesFilter.some((f) => rs.includes(f.toLowerCase()));
      });
    }
    locationsToProcess.forEach((event, idx) => {
      const lat = event?.latitude != null ? Number(event.latitude) : NaN;
      const lng = event?.longitude != null ? Number(event.longitude) : NaN;
      if (isNaN(lat) || isNaN(lng)) return;
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return; // out of range
      if (lat === 0 && lng === 0) return; // placeholder coords
      const keyBase = `${event.assetGuid || 'NO_GUID'}|${lat.toFixed(6)}|${lng.toFixed(6)}`;
      if (seen.has(keyBase)) return; // exact duplicate
      seen.add(keyBase);
      // React key: include index to differentiate if same guid appears at different coords
      const reactKey = `${event.assetGuid || 'NO_GUID'}-${lat.toFixed(6)}-${lng.toFixed(6)}-${idx}`;
      result.push({ ...event, lat, lng, __reactKey: reactKey });
    });
    return result;
  }, [wellLocations, wellStatusesFilter, searchedAsset]);

  // Filter markers based on visible statuses
  const filteredMarkers = React.useMemo(() => {
    return validMarkers.filter((marker) => {
      const status = getNormalizedStatus(marker.runStatus);
      return visibleStatuses.has(status);
    });
  }, [validMarkers, visibleStatuses]);

  // Compute an initial center/zoom for the map based on available markers
  const initialViewport = React.useMemo(() => {
    const list = filteredMarkers.length > 0 ? filteredMarkers : validMarkers;
    if (!list || list.length === 0) {
      // Fallback to a neutral world center
      return { center: { lat: 20, lng: 0 }, zoom: 3 };
    }
    if (list.length === 1) {
      return { center: { lat: list[0].lat, lng: list[0].lng }, zoom: 12 };
    }
    // Approximate bounds center by averaging lat/lng
    const sum = list.reduce(
      (acc, m) => ({ lat: acc.lat + m.lat, lng: acc.lng + m.lng }),
      { lat: 0, lng: 0 },
    );
    const avg = { lat: sum.lat / list.length, lng: sum.lng / list.length };
    return { center: avg, zoom: 4 };
  }, [filteredMarkers, validMarkers]);

  // Fetch data on initial component mount
  useEffect(() => {
    dispatch(fetchAssetsLocationDetails());
  }, [dispatch]);

  // Helper to build bounds from a set of markers
  const buildBoundsFromMarkers = useCallback((markers: Array<{ lat: number; lng: number }>) => {
    const b = new window.google.maps.LatLngBounds();
    markers.forEach((m) => b.extend({ lat: m.lat, lng: m.lng }));
    return b;
  }, []);

  // Automatically adjust map bounds to fit visible markers; fallback to all valid markers
  const fitBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    // Prefer currently visible markers; if none, fallback to all valid markers
    const markersToShow = filteredMarkers.length > 0 ? filteredMarkers : validMarkers;
    if (!markersToShow || markersToShow.length === 0) return;

    if (markersToShow.length > 1) {
      const bounds = buildBoundsFromMarkers(markersToShow);
      try {
        map.fitBounds(bounds, { top: 48, right: 48, bottom: 48, left: 48 } as google.maps.Padding);
        const list = filteredMarkers.length > 0 ? filteredMarkers : validMarkers;
        if (!list || list.length === 0) {
          // Fallback to a neutral world center
          return;
        }
        if (list.length === 1) {
          map.setCenter({ lat: list[0].lat, lng: list[0].lng });
          map.setZoom(12);
          return;
        }
        // Approximate bounds center by averaging lat/lng
        const sum = list.reduce(
          (acc, m) => ({ lat: acc.lat + m.lat, lng: acc.lng + m.lng }),
          { lat: 0, lng: 0 },
        );
        const avg = { lat: sum.lat / list.length, lng: sum.lng / list.length };
        map.setCenter(avg)
        map.setZoom(2);
        return;
      } catch (_) {
        map.fitBounds(bounds);
      }
      return;
    }

    // Single marker: center directly on it and ensure a sensible zoom
    const only = markersToShow[0];
    const pos = { lat: only.lat, lng: only.lng };
    map.setCenter(pos);
    const currentZoom = map.getZoom() ?? 3;
    const desiredZoom = 12; // city-level zoom
    map.setZoom(Math.max(currentZoom, desiredZoom));
  }, [filteredMarkers, validMarkers, buildBoundsFromMarkers]);

  useEffect(() => {
    // Fit bounds only when the map is loaded and markers are available
    if (isLoaded) {
      fitBounds();
      searchedAsset && filteredMarkers.length === 0 && showErrorToast('No valid well location to display.');
    }
  }, [isLoaded, fitBounds]);

  // Re-fit bounds whenever filters, search, or available markers change
  useEffect(() => {
    if (!isLoaded) return;
    fitBounds();
  }, [filteredMarkers, validMarkers, isLoaded, fitBounds]);

  // Compute and enforce a safe minZoom so the restricted map never shows grey bars
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;
    const r = RESTRICTION_BOUNDS_OBJ.current;
    restrictionBoundsRef.current = new window.google.maps.LatLngBounds(
      new window.google.maps.LatLng(r.south, r.west),
      new window.google.maps.LatLng(r.north, r.east),
    );

    const recomputeMinZoom = () => {
      if (!mapRef.current || !restrictionBoundsRef.current) return;
      const m = mapRef.current;
      const previousCenter = m.getCenter();
      const previousZoom = m.getZoom();
      // Temporarily fit to restriction to measure the minimum zoom for current viewport
      m.fitBounds(restrictionBoundsRef.current);
      const safeMin = m.getZoom() ?? 2;
      m.setOptions({ minZoom: safeMin });
      // Restore previous view and then re-fit markers to keep UX consistent
      if (previousCenter) m.setCenter(previousCenter);
      if (previousZoom != null) m.setZoom(previousZoom);
      // Ensure markers are nicely framed after any size/fullscreen change
      fitBounds();
    };

    // Initial compute and listeners for window resize/fullscreen
    recomputeMinZoom();
    const onResize = () => recomputeMinZoom();
    const onFsChange = () => recomputeMinZoom();
    window.addEventListener('resize', onResize);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => {
      window.removeEventListener('resize', onResize);
      document.removeEventListener('fullscreenchange', onFsChange);
    };
  }, [isLoaded, fitBounds]);

  const handleMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      // Defer first fit until map is idle to avoid race conditions
      try {
        google.maps.event.addListenerOnce(map, 'idle', () => {
          fitBounds();
        });
      } catch (_) {
        fitBounds();
      }
      // Create a container for custom controls
      const expandContainer = document.createElement('div');
      expandContainer.style.display = 'flex';
      expandContainer.style.margin = '16px';
      // expandContainer.style.marginTop = '8px';
      expandContainer.style.gap = '8px';
      expandContainer.style.alignItems = 'center';
      expandContainer.style.background = '#131F2E';
      expandContainer.style.border = '1px solid #4A5463';
      expandContainer.style.borderRadius = '8px';
      expandContainer.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';

      const zoomContainer = document.createElement('div');
      zoomContainer.style.display = 'flex';
      zoomContainer.style.gap = '8px';
      zoomContainer.style.flexDirection = 'column';
      zoomContainer.style.justifyContent = 'end';
      zoomContainer.style.padding = '16px';
      zoomContainer.style.boxShadow = '0 2px 6px rgba(0,0,0,0.3)';

      // Zoom in button
      const zoomInBtn = document.createElement('button');
      zoomInBtn.type = 'button';
      zoomInBtn.title = 'Zoom in';
      zoomInBtn.style.width = '32px';
      zoomInBtn.style.height = '32px';
      zoomInBtn.style.display = 'grid';
      zoomInBtn.style.placeItems = 'center';
      zoomInBtn.style.color = '#F7F9F9';
      zoomInBtn.style.background = '#131F2E';
      zoomInBtn.style.border = '1px solid #4A5463';
      zoomInBtn.style.borderRadius = '6px';
      zoomInBtn.style.cursor = 'pointer';
      zoomInBtn.style.boxShadow = '0 1px 2px 0 rgba(16, 24, 40, 0.05)';
      zoomInBtn.innerHTML = ReactDOMServer.renderToString(<ZoomInIcon />); // replace with your SVG
      zoomInBtn.onclick = () => {
        const z = map.getZoom() ?? 5;
        map.setZoom(Math.min(z + 1, 21));
      };

      // Zoom out button
      const zoomOutBtn = document.createElement('button');
      zoomOutBtn.type = 'button';
      zoomOutBtn.title = 'Zoom out';
      zoomOutBtn.style.width = '32px';
      zoomOutBtn.style.height = '32px';
      zoomOutBtn.style.display = 'grid';
      zoomOutBtn.style.cursor = 'pointer';
      zoomOutBtn.style.placeItems = 'center';
      zoomOutBtn.style.color = '#F7F9F9';
      zoomOutBtn.style.border = '1px solid #4A5463';
      zoomOutBtn.style.borderRadius = '6px';
      zoomOutBtn.style.background = '#131F2E';
      zoomOutBtn.style.boxShadow = '0 1px 2px 0 rgba(16, 24, 40, 0.05)';

      zoomOutBtn.innerHTML = ReactDOMServer.renderToString(<ZoomOutIcon />); // replace with your SVG
      zoomOutBtn.onclick = () => {
        const z = map.getZoom() ?? 5;
        map.setZoom(Math.max(z - 1, 0));
      };

      // Fullscreen toggle
      const fullscreenBtn = document.createElement('button');
      fullscreenBtn.type = 'button';
      fullscreenBtn.title = 'Fullscreen';
      fullscreenBtn.style.cursor = 'pointer';
      fullscreenBtn.style.placeItems = 'center';
      fullscreenBtn.style.width = '32px';
      fullscreenBtn.style.height = '32px';

      fullscreenBtn.innerHTML = ReactDOMServer.renderToString(<FullScreenIcon />); // replace with your SVG
      fullscreenBtn.onclick = () => {
        const el = map.getDiv();
        const isFs = document.fullscreenElement != null;
        if (!isFs) {
          el.requestFullscreen?.();
        } else {
          document.exitFullscreen?.();
        }
      };

      zoomContainer.appendChild(zoomInBtn);
      zoomContainer.appendChild(zoomOutBtn);
      expandContainer.appendChild(fullscreenBtn);

      // Position top-left; pick a ControlPosition that fits your layout
      map.controls[google.maps.ControlPosition.TOP_RIGHT].push(expandContainer);
      map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomContainer);
    },
    [fitBounds],
  );

  const handleMapClick = () => {
    setClickedMarker(null);
    if (hoverTimeoutRef.current) {
      window.clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  // Toggle legend status visibility
  const toggleLegendStatus = (status: string) => {
    setVisibleStatuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  };

  const handleMarkerClick = (marker: WellLocation & { lat: number; lng: number }) => {
    // Only set clicked marker; no auto-pan to avoid jump. If desired, add conditional pan when marker is outside current bounds.
    setClickedMarker(marker);
    setShowPeak(true);
    if (marker.assetGuid) {
      dispatch(fetchInitialStateAsync({ assetId: marker.assetGuid }));
    }
  };

  const handleAssetNameClick = () => {
    if (activeMarker?.assetGuid) {
      const selectedAsset = assets?.find((asset) => asset.assetId === activeMarker.assetGuid);
      if (selectedAsset && selectedAsset.assetId) {
        const formattedGroupName = selectedAsset?.groupName?.split(' ').join('_');
        navigate(`/asset/status/${selectedAsset.assetId}/${formattedGroupName}`);
      }
    }
  };

  // Avoid early return (helps prevent hot-reload hook order glitches)
  const showLoading = loading;

  const getRunTypeColor = (runType?: string): string => {
    if (!runType) return DEFAULT_RUN_STATUS_COLOR;
    const lower = runType.toLowerCase();
    for (const [status, color] of RUN_STATUS_COLOR_ENTRIES) {
      if (lower.includes(status)) return color;
    }
    return DEFAULT_RUN_STATUS_COLOR;
  };

  // Map runStatus to a CSS class (avoid inline styles for CSP compliance)
  const getRunStatusClass = (runStatus?: string): string => {
    if (!runStatus) return 'run-status--default';
    const lower = runStatus.toLowerCase();
    if (lower.includes('running')) return 'run-status--running';
    if (lower.includes('shutdown')) return 'run-status--shutdown';
    if (lower.includes('idle')) return 'run-status--idle';
    if (lower.includes('communication failure')) return 'run-status--comm-failure';
    if (lower.includes('disabled')) return 'run-status--disabled';
    return 'run-status--default';
  };

  // Helper to pick markup
  const getAssetMarkup = (id: number) => assetIconMarkup[id] || assetIconMarkup[0];

  // Helper: recolor the raw inner SVG markup by replacing existing stroke/fill tokens.
  const colorizeAssetMarkup = (markup: string, color: string) => {
    return (
      markup
        // stroke replacements (ignore 'none')
        .replace(/stroke="#F7F9F9"/gi, `stroke="${color}"`)
        .replace(/stroke="#ffffff"/gi, `stroke="${color}"`)
        // fill replacements (keep fill="none")
        .replace(/fill="#F7F9F9"/gi, `fill="${color}"`)
        .replace(/fill="#ffffff"/gi, `fill="${color}"`)
    );
  };

  // Updated: accept innerColor to recolor the inner markup.
  // Cache icons so object reference stays stable between renders (prevents unnecessary icon reload flicker)
  const iconCacheRef = useRef<Map<string, google.maps.Icon>>(new Map());

  const getCustomMarkerIcon = (pinColor: string, industryApplicationId: number, innerColor: string) => {
    const cacheKey = `${industryApplicationId}|${pinColor}|${innerColor}`;
    const cached = iconCacheRef.current.get(cacheKey);
    if (cached) return cached;
    let inner = getAssetMarkup(industryApplicationId || 0);
    inner = colorizeAssetMarkup(inner, innerColor);

    // Geometry constants
    const CIRCLE_CX = 18;
    const CIRCLE_CY = 18;
    const CIRCLE_R = 13;
    const INNER_BOX_SIZE = 20;
    const PADDING = 3;
    const maxInnerDiameter = CIRCLE_R * 2 - PADDING * 2;
    const scale = maxInnerDiameter / INNER_BOX_SIZE;
    const offset = (INNER_BOX_SIZE * scale) / 2;
    const translateX = CIRCLE_CX - offset;
    const translateY = CIRCLE_CY - offset;

    const svg = `
            <svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48">
            <!-- Outer pin -->
            <path d="M0 18C0 28.4 10.3 40.5 16.65 46.75c.74.73 1.93.74 2.69.01C25.64 40.7 36 28.8 36 18 36 4 23.5 0 18 0S0 4 0 18Z" fill="${pinColor}"/>
            <!-- Inner circle background -->
            <circle cx="${CIRCLE_CX}" cy="${CIRCLE_CY}" r="${CIRCLE_R}" fill="white" stroke="white" stroke-width="2"/>
            <!-- Centered inner asset icon -->
            <g transform="translate(${translateX.toFixed(3)} ${translateY.toFixed(3)}) scale(${scale.toFixed(4)})">
                ${inner}
            </g>
            </svg>`.trim();

    const iconObj = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(36, 48),
      anchor: new window.google.maps.Point(18, 48),
    };
    iconCacheRef.current.set(cacheKey, iconObj);
    return iconObj;
  };

  // determine if there are selected filters so SCSS can add spacing if needed


  const handleFilterChange = (newValue: string[]) => {
    const otherFailureStatuses = ['Error', 'fail', "Bad Item", "EmptyData", "illegal data address", "exception", "No Params", "CommandNotSupported", "RPCServUnvl"]
    if (newValue.includes('communication failure')) {
      setWellStatusesFilter(newValue.concat(otherFailureStatuses));
      return;
    }
    else setWellStatusesFilter(newValue.filter(ns => !otherFailureStatuses.includes(ns)));
  };
  return (
    <>
      <div
        className={`group-status-main-header asset-nav-bar`}
        style={{ paddingRight: '6px' }}
      >
        <div className='header-container'>
          <MenuNavIcon />
          <span className='group-status-group-name'>Locations</span>
        </div>
        <div className='custom-views-header-elements'>
          <HeaderElements showFilterIcon={false} />
        </div>
      </div>

      <div className='main-container well-location-main-container'>
        <div className='flex h-[68px] flex-col gap-[20px]  items-start self-stretch shrink-0 flex-nowrap relative z-[1] well-locations-header-border'>
          <div className='flex pt-[16px] pr-[24px] pb-0 pl-[24px] gap-[16px] items-start self-stretch shrink-0 flex-nowrap relative z-[2]'>
            <div className='flex flex-col gap-[4px] justify-center items-start grow shrink-0 basis-0 flex-nowrap relative z-[3]'>
              <div className='flex gap-[8px] items-center self-stretch shrink-0 flex-nowrap relative z-[4]'>
                <span className="h-[28px] shrink-0 basis-auto font-['Mulish'] text-[18px] font-semibold leading-[28px] text-[#f7f9f9] relative text-left whitespace-nowrap z-[5]">
                  Well locations
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`flex w-full pr-[24px] ${activeMarker ? `gap-[4px] ${showPeak ? 'pl-[10px]' : ''}` : 'pl-[24px]'} self-stretch shrink-0 flex-nowrap rounded-tl-[0px] rounded-tr-none rounded-br-none rounded-bl-[0px] border-solid border-t border-t-[#4a5463] relative`}
        >
          {showPeak && (
            <div
              className={`transition-all duration-200 ${activeMarker ? 'flex flex-col w-[390px] max-h-[calc(100vh-280px)] pb-5' : 'hidden'}`}
            >
              <div className='flex flex-col pt-[20px] px-[24px] pb-[16px]'>
                <div className='flex bg-[#253040] px-[12px] py-[12px] max-w-[390px] flex-col gap-[4px] items-start self-stretch shrink-0 flex-nowrap rounded-[8px] border-solid border border-[#4a5463] relative z-[1]'>
                  <div
                    className='flex w-auto min-w-[196px] max-w-[420px] gap-[8px] flex-wrap bg-cover bg-no-repeat relative z-[2] asset-name-header'
                  >
                    {activeMarker?.assetName && (
                      <span
                        className={`asset-name-text ${activeMarker.assetName.length > 200 ? 'clamp-2-lines' : ''}`}
                        title={activeMarker.assetName}
                        onClick={handleAssetNameClick}
                        style={{ cursor: 'pointer' }}
                      >
                        {activeMarker.assetName}
                      </span>
                    )}
                  </div>
                  <span className="h-[20px] shrink-0 basis-auto font-['Mulish'] text-[14px] font-normal leading-[20px] text-[#cdd5df] relative text-left whitespace-nowrap z-[3]">
                    {activeMarker?.customerName}
                  </span>
                </div>
                {isRodliftwellStatusLoading ? (
                  <Loader />
                ) : rodliftwellstatusData.length > 0 ? (
                  <RodLiftStatusOverviewTable data={rodliftwellstatusData} parentComponent='WellLocations' />
                ) : (
                  <div className='no-matching-records-container text-center p-4 pl-6 pr-6'>
                    <span className='no-matching-records-message'>No records found</span>
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            className={`flex  self-stretch shrink-0 flex-nowrap rounded-tl-none rounded-tr-none rounded-br-none rounded-bl-none border-solid ${activeMarker && showPeak ? 'gap-[16px] border-l border-l-[#4a5463]' : 'gap-[8px]'} relative`}
          >
            <div
              className={`transition-all duration-200 justify-center ${activeMarker ? 'flex flex-col w-[26px]' : 'hidden'}`}
            >
              <div className='flex flex-col text-center mt-2 cursor-pointer' onClick={() => setShowPeak(!showPeak)}>
                {showPeak ? <SliderCollapse /> : <SliderExpand />}
              </div>
            </div>
          </div>
          <div className='flex flex-col flex-1 container-backdrop min-h-[calc(100vh-280px)] pb-5 items-start flex-nowrap box-content  shadow-[0_1px_2px_0_rgba(16,24,40,0.05)]'>
            <div className='form-row'>
              <div className='w-[280px] py-[16px]'>
                <MultiSelectFormField
                  label='Well status'
                  name='wellstatus'
                  value={wellStatusesFilter}
                  onChange={(_e, n) => handleFilterChange(n)}
                  className=' !border !border-solid bg-inherit'
                  options={wellStatuses.map((c) => ({ key: c.key.toString(), value: c.value.toString() }))}
                />
              </div>
              <div className='w-full text-end pr-[4px]'>
                <GlobalWellHistorySearch
                  className={`max-w-[346px]  !text-[#f7f9f9] navigation-content-search header-elements-search ${isSearchExpanded ? 'expanded' : ''}`}
                  onIconClick={() => setIsSearchExpanded((prev) => !prev)}
                  onCollapse={() => setIsSearchExpanded(false)}
                  navigateToWell={false}
                  shouldSyncHistory={false}
                  showAssetsOnly={true}
                  placeholderText='Search'
                  showWellStatus={false}
                  onChange={(obj) => {
                    setClickedMarker(null);
                    setSearchedAsset(obj?.id && obj?.name ? { id: obj.id, name: obj.name || '' } : null);
                  }}
                  shouldKeepSelectedWell={true}
                  parentComponent='WellLocations'
                />
              </div>
            </div>
            {showLoading && <Loader />}

            {!showLoading &&
              isLoaded &&
              (hasValidMarkers ? (
                <GoogleMap
                  mapContainerStyle={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '8px',
                    borderColor: '#4A5463',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                  }}
                  center={initialViewport.center}
                  zoom={initialViewport.zoom}
                  onLoad={handleMapLoad}
                  onClick={handleMapClick} // Close info window on map click
                  options={{
                    disableDefaultUI: true,
                    streetViewControl: false, // Optional: clean up the UI
                    mapTypeControl: false,
                    fullscreenControl: false,
                    zoomControl: false,
                    cameraControl: false,
                    // minZoom is set dynamically after load to avoid grey bars
                    maxZoom: 18,
                    // Apply dark theme styling
                    styles: [
                      { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
                      { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
                      { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
                      {
                        featureType: 'administrative.locality',
                        elementType: 'labels.text.fill',
                        stylers: [{ color: '#d59563' }],
                      },
                      {
                        featureType: 'poi',
                        elementType: 'labels.text.fill',
                        stylers: [{ color: '#d59563' }],
                      },
                      {
                        featureType: 'poi.park',
                        elementType: 'geometry',
                        stylers: [{ color: '#263c3f' }],
                      },
                      {
                        featureType: 'poi.park',
                        elementType: 'labels.text.fill',
                        stylers: [{ color: '#6b9a76' }],
                      },
                      {
                        featureType: 'road',
                        elementType: 'geometry',
                        stylers: [{ color: '#38414e' }],
                      },
                      {
                        featureType: 'road',
                        elementType: 'geometry.stroke',
                        stylers: [{ color: '#212a37' }],
                      },
                      {
                        featureType: 'road',
                        elementType: 'labels.text.fill',
                        stylers: [{ color: '#9ca5b3' }],
                      },
                      {
                        featureType: 'road.highway',
                        elementType: 'geometry',
                        stylers: [{ color: '#746855' }],
                      },
                      {
                        featureType: 'road.highway',
                        elementType: 'geometry.stroke',
                        stylers: [{ color: '#1f2835' }],
                      },
                      {
                        featureType: 'road.highway',
                        elementType: 'labels.text.fill',
                        stylers: [{ color: '#f3d19c' }],
                      },
                      {
                        featureType: 'transit',
                        elementType: 'geometry',
                        stylers: [{ color: '#2f3948' }],
                      },
                      {
                        featureType: 'transit.station',
                        elementType: 'labels.text.fill',
                        stylers: [{ color: '#d59563' }],
                      },
                      {
                        featureType: 'water',
                        elementType: 'geometry',
                        stylers: [{ color: '#17263c' }],
                      },
                      {
                        featureType: 'water',
                        elementType: 'labels.text.fill',
                        stylers: [{ color: '#515c6d' }],
                      },
                      {
                        featureType: 'water',
                        elementType: 'labels.text.stroke',
                        stylers: [{ color: '#17263c' }],
                      },
                    ],
                    // Broad panning restriction to avoid infinite horizontal wrap while still including edge markers
                    restriction: {
                      latLngBounds: {
                        north: RESTRICTION_BOUNDS_OBJ.current.north,
                        south: RESTRICTION_BOUNDS_OBJ.current.south,
                        west: RESTRICTION_BOUNDS_OBJ.current.west,
                        east: RESTRICTION_BOUNDS_OBJ.current.east,
                      },
                      // Prevent wrap-around while keeping the viewport inside bounds
                      strictBounds: true,
                    },
                  }}
                >
                  {/* Custom Map Type Switch */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      height: '40px',
                      padding: '6px',
                      border: '1px solid #FFFFFF',
                      borderRadius: 8,
                      background: '#384252',
                      backdropFilter: 'blur(2px)',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    }}
                  >
                    {['Map', 'Satellite'].map((label) => {
                      const isMap = label === 'Map';
                      return (
                        <div
                          key={label}
                          onClick={() => {
                            if (!mapRef.current) return;
                            mapRef.current.setMapTypeId(isMap ? 'roadmap' : 'satellite');
                            setMapType(label);
                          }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 96,
                            height: 32,
                            padding: '4px 6px',
                            cursor: 'pointer',
                            fontFamily: 'Mulish, system-ui, sans-serif',
                            fontSize: 16,
                            borderRadius: 6,
                            fontWeight: 600,
                            color: '#F7F9F9',
                            background: mapType === label ? '#001023' : '#384252',
                            transition: 'all 120ms ease-in-out',
                          }}
                        >
                          {label}
                        </div>
                      );
                    })}
                  </div>
                  {/* --- 3. MARKER RENDERING: Map directly over the filtered `filteredMarkers` array --- */}
                  {filteredMarkers.map((marker) => {
                    const pinColor = getRunTypeColor(marker.runStatus);
                    const icon = getCustomMarkerIcon(pinColor, marker.industryApplicationId || 0, pinColor);
                    return (
                      <MarkerF
                        key={marker.__reactKey}
                        position={{ lat: marker.lat, lng: marker.lng }}
                        onClick={() => handleMarkerClick(marker)}
                        onMouseOver={() => {
                          if (hoverTimeoutRef.current) {
                            window.clearTimeout(hoverTimeoutRef.current);
                            hoverTimeoutRef.current = null;
                          }
                          setHoveredMarker(marker);
                        }}
                        onMouseOut={() => {
                          if (hoverTimeoutRef.current) {
                            window.clearTimeout(hoverTimeoutRef.current);
                          }
                          hoverTimeoutRef.current = window.setTimeout(() => {
                            setHoveredMarker((cur) => (cur?.__reactKey === marker.__reactKey ? null : cur));
                            hoverTimeoutRef.current = null;
                          }, 120);
                        }}
                        icon={icon}
                      />
                    );
                  })}
                  {hoveredMarker && (
                    <OverlayViewF
                      position={{ lat: hoveredMarker.lat, lng: hoveredMarker.lng }}
                      mapPaneName='overlayMouseTarget'
                      getPixelPositionOffset={(_width, height) => {
                        const arrow = OVERLAY_ARROW_SIZE;
                        const tipToCenterY = MARKER_CENTER_OFFSET_Y; // move tip to marker icon center
                        return {
                          x: Math.round(arrow * (0.5 + 1 / Math.SQRT2)) + TOOLTIP_TAIL_GAP_X,
                          // vertically center, then shift up by center offset
                          y: -Math.round(height / 2) - tipToCenterY,
                        };
                      }}
                    >
                      <div
                        className='wl-info-window'
                        role='dialog'
                        aria-label={hoveredMarker.assetName || 'Asset'}
                        style={{
                          position: 'relative',
                          background: '#1E293B',
                          padding: '12px 16px',
                          borderRadius: 8,
                          color: '#F7F9F9',
                          fontFamily: 'var(--default-font-family, system-ui, sans-serif)',
                          boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
                          pointerEvents: 'auto',
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '20px'
                        }}
                      >
                        {/* Arrow */}
                        <div
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            width: OVERLAY_ARROW_SIZE,
                            height: OVERLAY_ARROW_SIZE,
                            background: '#1E293B',
                            transform: 'translate(-50%, -50%) rotate(45deg)',
                            borderRadius: 2,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.35)',
                          }}
                        />
                        <div
                          className='wl-info-window__title'
                          style={{ color: '#FCFCFD', fontWeight: 600, fontSize: 16 }}
                        >
                          {hoveredMarker.assetName}
                        </div>
                        {hoveredMarker.runStatus && (
                          <div className={`wl-run-status-badge ${getRunStatusClass(hoveredMarker.runStatus)}`}>
                            <span
                              className='well-locations-legend__dot' style={{
                                width: '8px', height: '8px',
                                alignSelf: 'center'
                              }}

                            />
                            <span className='wl-run-status-badge__text'>{hoveredMarker.runStatus}</span>
                          </div>
                        )}
                      </div>
                    </OverlayViewF>
                  )}
                </GoogleMap>
              ) : (
                <div className='w-full h-full flex items-center justify-center'>
                  <EmptyState message='Well locations data currently not available. Please check again later.' />
                </div>
              ))}
          </div>
        </div>
        {hasValidMarkers && wellStatusesFilter?.length === 0 && (
          <div className='well-locations-legend' aria-label='Well status legend'>
            {LEGEND_ITEMS.map(({ status, color, label }) => (
              <div
                key={status}
                className={`well-locations-legend__item ${visibleStatuses.has(status) ? 'active' : 'inactive'}`}
                onClick={() => toggleLegendStatus(status)}
                style={{ cursor: 'pointer', opacity: visibleStatuses.has(status) ? 1 : 0.5 }}
              >
                <span
                  className='well-locations-legend__dot'
                  style={{
                    background: visibleStatuses.has(status) ? color : 'transparent',
                    border: `2px solid ${color}`,
                  }}
                />
                <span className='well-locations-legend__label'>{label}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default WellLocations;
